import { useEffect, useState } from 'react';
import { isNewDeployment, readServedBuild } from '../deployment';

/** Cada cuánto se pregunta con la pestaña abierta y a la vista. */
export const DEPLOYMENT_CHECK_MS = 5 * 60_000;

/** Lo que publica el build. Ver el plugin `buildId` en `vite.config.ts`. */
const VERSION_URL = '/version.json';

const fetchServedBuild = async (): Promise<string | null> => {
  /* `no-store`: la pregunta es qué hay en el servidor ahora, no qué se guardó. */
  const response = await fetch(VERSION_URL, { cache: 'no-store' });
  if (!response.ok) return null;
  return readServedBuild(await response.json());
};

export interface NewDeploymentOptions {
  /** El build de esta pestaña. */
  readonly running?: string;
  /**
   * Sólo en producción. En desarrollo no hay `version.json` —Vite no lo emite
   * fuera del build— y en el modo demo no hay nada que desplegar.
   */
  readonly enabled?: boolean;
  readonly fetchServed?: () => Promise<string | null>;
  readonly intervalMs?: number;
}

/**
 * `true` desde que se detecta que se desplegó otra versión después de cargar.
 *
 * Pregunta al montar, cada cinco minutos y **cada vez que se vuelve a la
 * pestaña**: esa última es la que importa, porque quien dejó el portal abierto
 * desde la mañana lo mira de nuevo justo cuando le avisan que hay algo nuevo.
 * Una vez que avisa deja de preguntar: la respuesta ya no va a cambiar.
 */
export const useNewDeployment = (options: NewDeploymentOptions = {}): boolean => {
  const {
    running = __BUILD_ID__,
    enabled = import.meta.env.PROD,
    fetchServed = fetchServedBuild,
    intervalMs = DEPLOYMENT_CHECK_MS,
  } = options;
  const [outdated, setOutdated] = useState(false);

  useEffect(() => {
    if (!enabled || outdated) return undefined;
    let cancelled = false;

    const check = async (): Promise<void> => {
      try {
        const served = await fetchServed();
        if (!cancelled && isNewDeployment(running, served)) setOutdated(true);
      } catch {
        /* Sin red no hay aviso: ver `isNewDeployment`. */
      }
    };
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void check();
    };

    void check();
    const timer = window.setInterval(() => {
      void check();
    }, intervalMs);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [enabled, outdated, fetchServed, running, intervalMs]);

  return outdated;
};

/**
 * Recargar sin caché.
 *
 * El HTML ya se sirve con `no-store` —ver `firebase.json`—, así que una recarga
 * normal trae el nuevo. Pedirlo antes con `cache: 'reload'` cubre al navegador
 * que se guardó una copia antes de que existiera ese encabezado.
 */
export const reloadWithoutCache = async (): Promise<void> => {
  try {
    await fetch(window.location.href, { cache: 'reload' });
  } catch {
    /* Se recarga igual. */
  }
  window.location.reload();
};
